/**
 * Oportunidades — lista del CRM V1 de Business, `OD-0048` D-07.
 *
 * Como el tablero de negocios de un CRM (petición del propietario, 2026-10-18):
 * arriba las tres cifras -abierto, ganado, perdido-, y lo abierto en un TABLERO
 * con una columna por etapa (`OD-0048-ENMIENDA-01`), cada una con cuántas hay y
 * lo que suman. LAS SUMAS LAS HACE LA API: esta web no hace aritmética de dinero
 * (`URS §129`). En escritorio, una tarjeta se ARRASTRA a otra columna para
 * cambiarla de etapa (`OD-0048-ENMIENDA-02` F-05) por la ruta de siempre; sin
 * posición dentro de la columna, que es lo que `RC-0016` aplaza. Y una caja
 * busca por título o cliente (F-03) sin tocar las cifras.
 *
 * Cada tarjeta lleva su probabilidad y, bajo las cifras, la previsión ponderada
 * (`OD-0058` D-05 y D-06), que también suma la API. Y encima del tablero, "José
 * te recomienda" (`OD-0058-ENMIENDA-01` E-06): las tres abiertas a las que
 * llamar primero. Las elige la API; aquí solo se pintan, en su orden.
 *
 * Con la ENMIENDA-02 de `OD-0058` (2026-10-29), todas las abiertas llevan número:
 * las que no están cualificadas, el de su etapa, y la etiqueta lo dice («Sin
 * cualificar», «Provisional»). Así cuentan en la previsión y en «José te
 * recomienda». Y al moverla de columna sin cualificar se invita a hacerlo.
 *
 * Fuera de Business la API contesta 402 y la pantalla explica que es de
 * Business, con un enlace a Mi plan (`D-04`). No es un error.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ETAPAS_ABIERTAS, type EstadoOportunidad, type EtapaAbierta, type ListaOportunidades } from "../api/cliente";
import { t } from "../i18n/es";
import { coincideBusqueda } from "../ui/clientes-parecidos";
import { diaLegible, importeLegible } from "../ui/formato";
import { estadoCualificacion, fechaEnDias, respondidas as cuantasRespondidas } from "../ui/jose";
import { Icono } from "../ui/Iconos";
import { Aviso, Boton, FilaPulsable, Tarjeta } from "../ui/primitivas";
import { FotoJose } from "./Jose";

type Estado =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "error" }
  | { readonly tipo: "sin-permiso" }
  | { readonly tipo: "solo-business" }
  | { readonly tipo: "datos"; readonly lista: ListaOportunidades };

const PESTANAS: readonly EstadoOportunidad[] = ["OPEN", "WON", "LOST"];

/** E-07: el puesto en "José te recomienda", tal como lo cuenta la API. */
const PUESTOS = ["1", "2", "3"] as const;

/** Vive en `ui/formato.ts`; se sigue exportando desde aquí para las pantallas que ya lo importan. */
export { diaLegible };

/**
 * OD-0058 D-06: el porcentaje de éxito con su número escrito y un color que
 * acompaña. `null` es "Sin cualificar", nunca un 0 %.
 *
 * ENMIENDA-02: con `origen`, la etiqueta dice de dónde sale. Sin cualificar
 * (G-03) va en gris y con su palabra, «Sin cualificar» o «Provisional»: es el
 * número de la etapa, no el de las respuestas. A mano (G-08), «A mano».
 */
export function EtiquetaProbabilidad({
  valor,
  origen = null,
  respondidas = 0,
}: {
  valor: number | null;
  origen?: string | null;
  respondidas?: number;
}) {
  if (valor === null) return <span className="insignia crm-prob crm-prob-sin">{t.oportunidades.sinCualificar}</span>;
  const porcentaje = t.oportunidades.porcentaje(valor);
  if (origen === "STAGE") {
    const palabra = respondidas === 0 ? t.oportunidades.sinCualificar : t.oportunidades.provisional;
    return <span className="insignia crm-prob crm-prob-sin">{`${porcentaje} · ${palabra}`}</span>;
  }
  const nivel = valor < 35 ? "baja" : valor < 65 ? "media" : "alta";
  return <span className={`insignia crm-prob crm-prob-${nivel}`}>{origen === "MANUAL" ? `${porcentaje} · ${t.oportunidades.aManoCorto}` : porcentaje}</span>;
}

export function SoloBusiness({ onVerPlan }: { onVerPlan: () => void }) {
  return (
    <div className="estado-vacio">
      <span className="estado-vacio-icono" aria-hidden="true">
        <Icono nombre="oportunidades" />
      </span>
      <p className="estado-vacio-titulo">{t.oportunidades.soloBusinessTitulo}</p>
      <p>{t.oportunidades.soloBusiness}</p>
      <Boton onClick={onVerPlan}>{t.oportunidades.verPlanes}</Boton>
    </div>
  );
}

export function PantallaOportunidades({
  pestana,
  onPestana,
  busqueda,
  onBusqueda,
  onAbrir,
  onNueva,
  onVerPlan,
  onSesionCaducada,
}: {
  /** La lleva el Panel: al volver de una oportunidad sigue en la misma pestaña. */
  pestana: EstadoOportunidad;
  onPestana: (p: EstadoOportunidad) => void;
  /** F-03: lo que se busca. También lo lleva el Panel, para que sobreviva a abrir una. */
  busqueda: string;
  onBusqueda: (texto: string) => void;
  /** `cualificar`: abrirla con José invitando a responder (G-08). */
  onAbrir: (id: string, cualificar?: boolean) => void;
  onNueva: () => void;
  onVerPlan: () => void;
  onSesionCaducada: () => void;
}) {
  const [estado, setEstado] = useState<Estado>({ tipo: "cargando" });
  // Sin ninguna oportunidad en NINGUNA pestaña se explica para qué sirve esto.
  // Se averigua una vez; una vez que hay alguna, ya no se vuelve a preguntar.
  const [hayAlguna, setHayAlguna] = useState<boolean | null>(null);
  const sabido = useRef(false);
  // F-05: la tarjeta que se arrastra, la columna sobre la que está y el aviso si
  // la API no deja moverla.
  const [arrastrada, setArrastrada] = useState<string | null>(null);
  const [destino, setDestino] = useState<EtapaAbierta | null>(null);
  const [avisoMover, setAvisoMover] = useState<string | null>(null);
  // G-08: la que se acaba de mover sin cualificar, para invitar a hacerlo.
  const [movidaSinCualificar, setMovidaSinCualificar] = useState<{ readonly id: string; readonly titulo: string; readonly porcentaje: number } | null>(null);

  const cargar = useCallback(async (): Promise<void> => {
    setEstado({ tipo: "cargando" });
    const r = await api.listarOportunidades(pestana);
    if (!r.ok) {
      if (r.estado === 401) onSesionCaducada();
      else if (r.estado === 402) setEstado({ tipo: "solo-business" });
      else setEstado({ tipo: r.estado === 403 ? "sin-permiso" : "error" });
      return;
    }
    if (!sabido.current) {
      let alguna = r.datos.oportunidades.length > 0 || r.datos.abiertas > 0;
      if (!alguna) {
        const todas = await api.listarOportunidades(null);
        alguna = todas.ok ? todas.datos.oportunidades.length > 0 : true;
      }
      sabido.current = alguna;
      setHayAlguna(alguna);
    }
    setEstado({ tipo: "datos", lista: r.datos });
  }, [pestana, onSesionCaducada]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (estado.tipo === "solo-business") {
    return (
      <Tarjeta titulo={t.oportunidades.titulo}>
        <SoloBusiness onVerPlan={onVerPlan} />
      </Tarjeta>
    );
  }

  if (estado.tipo === "datos" && hayAlguna === false) {
    return (
      <Tarjeta titulo={t.oportunidades.titulo}>
        <div className="estado-vacio">
          <span className="estado-vacio-icono" aria-hidden="true">
            <Icono nombre="oportunidades" />
          </span>
          <p className="estado-vacio-titulo">{t.oportunidades.vacioTitulo}</p>
          <p>{t.oportunidades.vacio}</p>
          <Boton onClick={onNueva}>
            <Icono nombre="mas" />
            {t.oportunidades.crearPrimera}
          </Boton>
        </div>
      </Tarjeta>
    );
  }

  const lista = estado.tipo === "datos" ? estado.lista : null;
  const hoy = fechaEnDias(new Date(), 0);
  // F-03: filtra lo que ya trajo la lista. Las cifras y «José te recomienda» no cambian.
  const visibles = lista === null ? [] : lista.oportunidades.filter((o) => coincideBusqueda(busqueda, o.title, o.customer?.displayName ?? null));
  const buscando = busqueda.trim() !== "";

  /** F-05: soltar en otra columna cambia la etapa, con las reglas de la ficha. */
  async function mover(id: string, etapa: EtapaAbierta): Promise<void> {
    setArrastrada(null);
    setDestino(null);
    const o = lista?.oportunidades.find((x) => x.id === id);
    if (o === undefined || o.stage === etapa) return;
    setAvisoMover(null);
    setMovidaSinCualificar(null);
    const r = await api.cambiarEtapaOportunidad(id, etapa);
    if (!r.ok) {
      if (r.estado === 401) return onSesionCaducada();
      setAvisoMover(
        r.estado === 403
          ? t.oportunidades.sinPermisoMover
          : r.estado === 409 && r.error?.motivo === "QUOTE_DRIVES_STAGE"
            ? t.oportunidad.etapaDelPresupuesto
            : r.estado === 0
              ? t.comun.sinRed
              : t.comun.errorGenerico,
      );
      return;
    }
    const movida = r.datos.oportunidad;
    if (estadoCualificacion(movida) !== null && movida.probability !== null) {
      setMovidaSinCualificar({ id: movida.id, titulo: movida.title, porcentaje: movida.probability });
    }
    await cargar();
  }
  const cifra = (c: { readonly cuantas: number; readonly total: string } | null) =>
    c === null ? "" : t.oportunidades.cifra(c.cuantas, importeLegible(c.total));

  return (
    <Tarjeta titulo={t.oportunidades.titulo}>
      {/* Sin pestañas aparte: las tres cifras de abajo SON las pestañas. */}
      <div className="barra-acciones barra-acciones-fin">
        <Boton onClick={onNueva} deshabilitado={estado.tipo !== "datos"}>
          <Icono nombre="mas" />
          {t.oportunidades.nueva}
        </Boton>
      </div>

      {/* Las tres cifras de la cabecera, como en un CRM: cada una lleva a su vista. */}
      {lista !== null ? (
        <div className="crm-cifras" role="group" aria-label={t.oportunidades.filtro}>
          {PESTANAS.map((p) => (
            <button
              key={p}
              type="button"
              className={`crm-cifra crm-cifra-${p}`}
              aria-pressed={pestana === p}
              onClick={() => onPestana(p)}
            >
              <span className="crm-cifra-etiqueta">{t.oportunidades.estados[p]}</span>
              <span className="crm-cifra-importe">
                {importeLegible(p === "OPEN" ? lista.totalAbierto : ((p === "WON" ? lista.ganadas : lista.perdidas)?.total ?? "0,00"))} €
              </span>
              <span className="crm-cifra-cuantas">
                {t.oportunidades.cuantas(p === "OPEN" ? lista.abiertas : ((p === "WON" ? lista.ganadas : lista.perdidas)?.cuantas ?? 0))}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {/* OD-0058 D-05: lo abierto ponderado por su probabilidad. Lo suma la API. */}
      {lista !== null && lista.prevision !== null && pestana === "OPEN" && lista.abiertas > 0 ? (
        <p className="crm-prevision">
          <span>
            {t.oportunidades.prevision}: <span className="crm-prevision-importe">{importeLegible(lista.prevision.total)} €</span>
          </span>
          <span className="ayuda-neutra">{t.oportunidades.previsionDetalle(lista.prevision.cuenta, lista.abiertas, lista.prevision.porEtapa)}</span>
        </p>
      ) : null}

      {lista !== null && pestana === "OPEN" && lista.abiertas > 0 ? (
        <section className="jose-recomienda" aria-label={t.jose.recomienda.titulo}>
          <div className="jose-cabecera">
            <FotoJose pequena />
            <div>
              <p className="jose-nombre">{t.jose.recomienda.titulo}</p>
              <p className="ayuda-neutra">{lista.recomendadas.length > 0 ? t.jose.recomienda.detalle : t.jose.recomienda.vacio}</p>
            </div>
          </div>
          {lista.recomendadas.length > 0 ? (
            <ol className="jose-recomendadas">
              {lista.recomendadas.flatMap((id, i) => {
                const o = lista.oportunidades.find((x) => x.id === id);
                return o === undefined || o.probability === null || o.estimatedAmount === null
                  ? []
                  : [
                      <li key={id}>
                        <button
                          type="button"
                          className="jose-recomendada"
                          onClick={() => {
                            // E-07: qué puesto se abre. Un contador sin ids; si falla, da igual.
                            const puesto = PUESTOS[i];
                            if (puesto !== undefined) void api.senalJose({ type: "RECOMMENDATION_OPENED", value: puesto });
                            onAbrir(id);
                          }}
                        >
                          <span className="tablero-tarjeta-titulo">{o.title}</span>
                          <span className="tablero-tarjeta-cliente">{o.customer?.displayName ?? t.oportunidades.clienteEliminado}</span>
                          <span className="ayuda-neutra">
                            {t.jose.recomienda.motivo(importeLegible(o.estimatedAmount), o.probability, o.probabilitySource === "STAGE")}
                          </span>
                        </button>
                      </li>,
                    ];
              })}
            </ol>
          ) : null}
        </section>
      ) : null}

      {lista !== null && (lista.oportunidades.length > 0 || buscando) ? (
        <div className="crm-busqueda">
          <label htmlFor="opo-buscar">{t.oportunidades.buscar}</label>
          <input
            id="opo-buscar"
            type="search"
            value={busqueda}
            placeholder={t.oportunidades.buscarMarcador}
            autoComplete="off"
            onChange={(e) => onBusqueda(e.target.value)}
          />
          {buscando ? (
            <p className="ayuda-neutra" aria-live="polite">
              {visibles.length === 0 ? t.oportunidades.sinResultados(busqueda.trim()) : t.oportunidades.resultados(visibles.length)}
            </p>
          ) : null}
        </div>
      ) : null}

      {avisoMover !== null ? <Aviso tono="aviso">{avisoMover}</Aviso> : null}
      {movidaSinCualificar !== null ? (
        <div className="crm-invitacion" role="status">
          <FotoJose pequena />
          <p>{t.oportunidades.movidaSinCualificar(movidaSinCualificar.titulo, movidaSinCualificar.porcentaje)}</p>
          <Boton variante="secundario" onClick={() => onAbrir(movidaSinCualificar.id, true)}>
            {t.oportunidades.cualificarAhora}
          </Boton>
        </div>
      ) : null}

      {estado.tipo === "cargando" ? <p>{t.oportunidades.cargando}</p> : null}
      {estado.tipo === "sin-permiso" ? <Aviso tono="aviso">{t.oportunidades.sinPermiso}</Aviso> : null}
      {estado.tipo === "error" ? (
        <>
          <Aviso tono="error">{t.oportunidades.error}</Aviso>
          <Boton variante="secundario" onClick={() => void cargar()}>
            {t.oportunidades.reintentar}
          </Boton>
        </>
      ) : null}

      {/*
        EL TABLERO: una columna por etapa abierta, con cuantas hay y lo que suman
        (lo suma la API). En escritorio se arrastra de una columna a otra (F-05);
        en el movil, y con el teclado, se cambia de etapa en la ficha. En el movil
        las columnas se desplazan de lado.
      */}
      {lista !== null && pestana === "OPEN" ? (
        <div className="tablero" role="list" aria-label={t.oportunidades.tablero}>
          {ETAPAS_ABIERTAS.map((e) => {
            const columna = visibles.filter((o) => o.stage === e);
            const resumen = lista.etapas.find((x) => x.etapa === e) ?? null;
            return (
              // Una etapa vacia ocupa menos: con una sola oportunidad, cinco
              // columnas iguales eran cuatro cajas vacias (auditoria, 2026-10-19).
              <section
                key={e}
                className={`tablero-columna${columna.length === 0 ? " tablero-columna-vacia" : ""}${destino === e ? " tablero-columna-destino" : ""}`}
                role="listitem"
                aria-label={t.oportunidades.etapas[e]}
                onDragOver={(ev) => {
                  if (arrastrada === null) return;
                  ev.preventDefault();
                  if (destino !== e) setDestino(e);
                }}
                onDragLeave={() => setDestino((d) => (d === e ? null : d))}
                onDrop={(ev) => {
                  ev.preventDefault();
                  const id = ev.dataTransfer.getData("text/plain") || arrastrada;
                  if (id !== null && id !== "") void mover(id, e);
                }}
              >
                <header className="tablero-cabecera">
                  <span className="tablero-titulo">{t.oportunidades.etapas[e]}</span>
                  <span className="tablero-cuenta">{resumen?.cuantas ?? columna.length}</span>
                </header>
                <p className="tablero-total">{resumen === null ? "" : `${importeLegible(resumen.total)} €`}</p>
                {columna.length === 0 ? <p className="tablero-vacia">{t.oportunidades.columnaVacia}</p> : null}
                {columna.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    className={arrastrada === o.id ? "tablero-tarjeta tablero-tarjeta-arrastrada" : "tablero-tarjeta"}
                    onClick={() => onAbrir(o.id)}
                    // Con presupuesto enlazado, la etapa la marca el presupuesto (D-E2): no se arrastra.
                    draggable={o.quote === null}
                    onDragStart={(ev) => {
                      ev.dataTransfer.setData("text/plain", o.id);
                      ev.dataTransfer.effectAllowed = "move";
                      setArrastrada(o.id);
                    }}
                    onDragEnd={() => {
                      setArrastrada(null);
                      setDestino(null);
                    }}
                  >
                    <span className="tablero-tarjeta-titulo">{o.title}</span>
                    <span className="tablero-tarjeta-cliente">{o.customer?.displayName ?? t.oportunidades.clienteEliminado}</span>
                    <span className="tablero-tarjeta-importe">
                      {o.estimatedAmount === null ? t.oportunidades.sinImporte : `${importeLegible(o.estimatedAmount)} €`}
                    </span>
                    {o.expectedCloseDate !== null ? (
                      <span className="tablero-tarjeta-fecha">
                        {t.oportunidades.previstaPara(diaLegible(o.expectedCloseDate))}
                        {/* Abierta y con la fecha ya pasada: se marca, no se esconde. */}
                        {o.expectedCloseDate < hoy ? <span className="insignia crm-atrasada">{t.oportunidades.atrasada}</span> : null}
                      </span>
                    ) : null}
                    <EtiquetaProbabilidad valor={o.probability} origen={o.probabilitySource} respondidas={cuantasRespondidas(o.qualification)} />
                    {lista.recomendadas.includes(o.id) ? <span className="insignia jose-marca">{t.jose.recomienda.marca}</span> : null}
                  </button>
                ))}
              </section>
            );
          })}
        </div>
      ) : null}

      {lista !== null && pestana !== "OPEN" && lista.oportunidades.length === 0 ? (
        <Aviso tono="info">{t.oportunidades.vacioEstado[pestana]}</Aviso>
      ) : null}

      {lista !== null && pestana !== "OPEN" && lista.oportunidades.length > 0 ? (
        <>
          <p className="ayuda-neutra">{cifra(pestana === "WON" ? lista.ganadas : lista.perdidas)}</p>
          <ul className="lista-filas">
            {visibles.map((o) => (
              <FilaPulsable key={o.id} onClick={() => onAbrir(o.id)}>
                <span className="fila-titulo">
                  {o.title} · {o.customer?.displayName ?? t.oportunidades.clienteEliminado}
                </span>
                <span className="ayuda-neutra">
                  {o.estimatedAmount === null ? t.oportunidades.sinImporte : t.oportunidades.importe(importeLegible(o.estimatedAmount))}
                  {" · "}
                  {t.oportunidades.cerradaEn(t.oportunidades.etapas[o.openStage])}
                  {/* F-01: por qué se perdió, si se dijo. */}
                  {o.status === "LOST" && o.lostReason !== null ? ` · ${t.oportunidad.motivos[o.lostReason]}` : ""}
                </span>
              </FilaPulsable>
            ))}
          </ul>
        </>
      ) : null}
    </Tarjeta>
  );
}
